import {
  MultipleColumnLayout,
  MultipleColumnLayoutProps,
} from '@doltech/core/lib/responsive/layout/MultipleColumnLayout';
import { IconOtherStar } from '@doltech/icons/IconOtherStar';
import { components } from '@doltech/pageManagement/dto';
import { Button } from '@doltech/ui/lib/figma/ButtonV2/ButtonV2';
import { Meta } from '@doltech/ui/lib/figma/Card/DolCard/components/Meta';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Typography } from '@doltech/ui/lib/figma/DesignSystemV2/Typography';
import { Link } from '@doltech/ui/lib/figma/Link';
import { deviceBreakpoints } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { PageSectionStructureV2 } from '@doltech/ui/lib/shared/components/PageSectionStructure/PageSectionStructureV2';
import { MONTH_DAY_NAME_FORMAT } from '@doltech/utils/lib/constants';
import { formatDate } from '@doltech/utils/lib/date';
import { getLandingUrlFromPath } from '@doltech/utils/lib/url';
import { noop } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { Grid } from 'styled-css-grid';

import { fetchHomeTrending } from '../api/home-page.api';
import { useGetAuthInfo } from '@doltech/core/lib/auth/withGlobalUserAuthInfo';

export const TrendingWrapper = styled.div`
  margin: 0 auto;
  display: flex;
  flex-direction: column;
`;

const TrendingContent = styled(MultipleColumnLayout)<MultipleColumnLayoutProps>`
  > svg {
    cursor: pointer;
  }
`;

const StyledLink = styled(Link)`
  &:hover {
    text-decoration: underline ${colorsV2.black100};
  }
`;
export const TrendingCardDetail = styled(Grid)`
  > div:nth-child(3n) {
    margin-top: 4px;
  }

  > div:nth-child(4n) {
    margin-top: 8px;
  }

  @media ${deviceBreakpoints.mobile} {
    overflow: hidden;
  }
`;

export const Dot = styled.div<{ size: number; color: string }>`
  width: ${(p) => p.size}px;
  height: ${(p) => p.size}px;
  background-color: ${(p) => p.color};
  border-radius: 50%;
`;

interface Props {
  data: components['schemas']['TrendingPageResponseDTO'][];
  isUpdateMode?: boolean;
  onUpdateAction?: () => void;
  pageSection?: components['schemas']['PageSectionDTO'];
  customTitle?: React.ReactNode;
  customDescription?: React.ReactNode;
}

export const Wrapper = styled.div``;

const TrendingList = ({ data, trendingDataWithOverviews, isUpdateMode, onUpdateAction }) => {
  if (!data || data.length === 0) {
    return null;
  }

  return (
    <Wrapper>
      <TrendingContent className="trending-content" itemMinWidth={300} rowGap={32} columnGap={24}>
        {(trendingDataWithOverviews ?? data).map((item, index) => (
          <TrendingCardDetail
            columns="minmax(0, 1fr)"
            rows="max-content max-content max-content auto"
            gap="4px"
            key={index}
          >
            <div>
              <Meta
                metadata={[
                  {
                    id: 'date',
                    text: formatDate(item.lastModifiedAt, MONTH_DAY_NAME_FORMAT),
                  },
                  {
                    id: 'author',
                    text: 'DOL IELTS Đình Lực',
                  },
                ]}
              />
            </div>
            <StyledLink href={getLandingUrlFromPath(item.url)}>
              <Typography.Paragraph
                color="black100"
                className="title"
                variant="semi-bold/16-24"
                ellipsis={{ rows: 2 }}
              >
                {item.title}
              </Typography.Paragraph>
            </StyledLink>
          </TrendingCardDetail>
        ))}
      </TrendingContent>
      {isUpdateMode && (
        <Button.Secondary fullWidth onClick={onUpdateAction}>
          Update
        </Button.Secondary>
      )}
    </Wrapper>
  );
};

export const TrendingComponent = observer((props: Props) => {
  const {
    data,
    isUpdateMode = false,
    onUpdateAction = noop,
    pageSection,
    customTitle,
    customDescription,
  } = props;
  const { isLoggedIn } = useGetAuthInfo();

  const { data: trendingDataWithOverviews } = useQuery<any>(
    [],
    () => {
      return fetchHomeTrending();
    },
    {
      enabled: Boolean(isLoggedIn),
    }
  );

  return (
    <PageSectionStructureV2
      oldStyling
      icon={<IconOtherStar style={{ color: colorsV2.yellow100, fontSize: 38 }} />}
      title={customTitle || 'Tin tức tiếng Anh'}
      description={customDescription}
      pageSection={pageSection}
      isAdmin
      content={
        <TrendingList
          data={data}
          isUpdateMode={isUpdateMode}
          onUpdateAction={onUpdateAction}
          trendingDataWithOverviews={trendingDataWithOverviews}
        />
      }
    />
  );
});
